import { Spinner } from './feedback';
import { useRef, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import { errorMessage } from '../app-context';
import { AuthField } from './AuthFields';

export function LoginForm({ devLogin, onDone }: { devLogin: boolean; onDone: () => Promise<void> }) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState(false);
  const [recovery, setRecovery] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const reset = () => {
    setChallenge(false);
    setPassword('');
    setCode('');
    setRecovery(false);
  };
  const run = async (action: () => Promise<void>) => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(undefined);
    try {
      await action();
    } catch (caught) {
      reset();
      setError(errorMessage(caught));
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  const verify = (value: string) =>
    run(async () => {
      await api.verifyLogin(value);
      reset();
      await onDone();
    });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (challenge) void verify(code);
    else
      void run(async () => {
        await api.login(login, password);
        setPassword('');
        setCode('');
        setRecovery(false);
        setChallenge(true);
      });
  };
  const changeCode = (value: string) => {
    setCode(value);
    if (!recovery && /^[0-9]{6}$/.test(value)) void verify(value);
  };
  return (
    <>
      {error && (
        <div className="alert danger" role="alert">
          {error}
        </div>
      )}
      <form className="stack" onSubmit={submit}>
        {challenge ? (
          <>
            <p>
              {recovery
                ? '保存したリカバリーコードを入力してください。'
                : '認証アプリの数字6桁を入力すると、自動でログインします。'}
            </p>
            <AuthField
              key={recovery ? 'recovery' : 'totp'}
              label={recovery ? 'リカバリーコード' : 'ワンタイムコード（6 桁）'}
              value={code}
              onChange={changeCode}
              code
              totpOnly={!recovery}
              recoveryOnly={recovery}
              disabled={busy}
              autoFocus
            />
            {recovery && (
              <button className="btn primary" disabled={busy}>
                {busy && <Spinner />}
                ログイン
              </button>
            )}
            <button
              className="btn ghost"
              type="button"
              disabled={busy}
              onClick={() => {
                setRecovery(!recovery);
                setCode('');
              }}
            >
              {busy && <Spinner />}
              {recovery ? '認証アプリのコードを使う' : 'リカバリーコードを使う'}
            </button>
            <button
              className="btn"
              type="button"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await api.cancelLogin();
                  reset();
                })
              }
            >
              {busy && <Spinner />}
              戻る
            </button>
          </>
        ) : (
          <>
            <AuthField label="ログイン ID" value={login} onChange={setLogin} login disabled={busy} />
            <AuthField label="パスワード" value={password} onChange={setPassword} password disabled={busy} />
            <button className="btn primary" disabled={busy}>
              {busy && <Spinner />}
              次へ
            </button>
          </>
        )}
      </form>
      {devLogin && !challenge && (
        <button
          className="btn ghost small"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              await api.devLogin();
              reset();
              await onDone();
            })
          }
        >
          {busy && <Spinner />}
          開発用ログイン（ローカルのみ）
        </button>
      )}
    </>
  );
}
